// @flow
import React from 'react';
import { Link } from 'react-router-dom';
import { first } from 'lodash';
import { Badge, Select } from 'bs';
import styled from 'styled-components';
import { mergeClassNames } from 'utils/functions';

const TabContent = styled.div`
    border: 1px solid #dee2e6;
    border-top: none;
    margin-bottom: 1rem;
    .table {
        margin-bottom: 0;
    }
    .badge {
        margin-left: 0.5rem;
    }
`;

const PillContent = styled.div`
    margin-bottom: 1rem;
    .table {
        margin-bottom: 0;
    }
    .badge {
        margin-left: 0.5rem;
    }
`;

const Anchor = ({ active, to, onClick, children, disabled }) => {
    const className = mergeClassNames(
        'nav-link',
        active ? 'active' : '',
        disabled ? 'disabled': '',
    );

    if (to) {
        return (
            <Link className={className} to={to}>
                {children}
            </Link>
        );
    } else {
        return (
            <a className={className} href='#link' onClick={onClick}>
                {children}
            </a>
        );
    }
};

const HorizontalTabs = ({ activeTab, selectTab, tabs, pills = false }) => {
    const className = `nav nav-${pills ? 'pills' : 'tabs'}`;

    return (
        <ul className={className}>
            {tabs.map(tab => (
                <li className='nav-item' key={tab.key}>
                    <Anchor
                        active={activeTab === tab.key}
                        disabled={tab.disabled}
                        to={tab.to && tab.to}
                        onClick={
                            selectTab &&
                            (e => {
                                e.preventDefault();
                                selectTab(tab.key);
                            })
                        }
                    >
                        {tab.title}
                        {tab.badge && (
                            <Badge flavor={tab.badgeFlavor} className='ml-2'>
                                {tab.badge}
                            </Badge>
                        )}
                    </Anchor>
                </li>
            ))}
        </ul>
    );
};

const SelectTabs = ({ activeTab, selectTab, tabs }) => {
    return (
        <Select onChange={e => selectTab(e.target.value)} value={activeTab}>
            {activeTab && (
                <option disabled='disabled'>
                    Select...
                </option>
            )}
            {tabs.map(tab => (
                <option key={tab.key} value={tab.key} disabled={tab.disabled} >
                    {tab.title}
                    {tab.badge && tab.badge}
                </option>
            ))}
        </Select>
    );
};

type Tab = {
    key: string,
    title: string,
    render: (props: Object) => void,
    badges: boolean,
    badgeFlavor: string,
    disabled: boolean,
    /** optional path to render as a Link. */
    to?: string,
};

type Props = {
    /** List of Tab data to build Tabs with */
    tabs: Array<Tab>,
    /** key of active tab */
    activeTab: string,
    /** You can pass a 'selectTab' callback on tab select.
     * You should either pass this or specify a 'to:' path
     * for each tab
     * */
    selectTab?: string => void,

    /** render tabs as tabs (default), pills, or select menu */
    tabStyle: 'default' | 'pills' | 'select',

    /** Additional props passed to tab render method */
    extraProps: Object,
};

export const Tabs = ({
    activeTab,
    selectTab,
    tabs,
    tabStyle = 'default',
    extraProps = {},
    ...domProps
}: Props) => {
    const tab = first(tabs.filter(t => t.key === activeTab));

    let Content = TabContent;
    if (tabStyle === 'pills' || tabStyle === 'select') {
        Content = PillContent;
    }

    let TabComponent = HorizontalTabs;
    if (tabStyle === 'select') {
        TabComponent = SelectTabs;
    }

    return (
        <div {...domProps}>
            <TabComponent
                tabs={tabs}
                selectTab={selectTab}
                activeTab={activeTab}
                pills={tabStyle === 'pills'}
            />
            <Content className='tab-content bg-white p-3'>
                <div className='tab-pane active' id={activeTab}>
                    {tab.render(extraProps)}
                </div>
            </Content>
        </div>
    );
};

export default Tabs;
